import { z } from 'zod';
import { ROLES } from './roles.js';
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
/**
 * Password policy: length is the dominant factor, so we require a long-ish
 * passphrase rather than a zoo of character-class rules.
 */
export const passwordSchema = z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .max(200, 'Password must be at most 200 characters');
export const displayNameSchema = z.string().trim().min(1).max(120);
export const bootstrapRequestSchema = z.object({
    bootstrapKey: z.string().min(1, 'Bootstrap key is required'),
    email: emailSchema,
    displayName: displayNameSchema,
    password: passwordSchema,
});
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required').max(200),
});
export const roleSchema = z.enum(ROLES);
export const publicUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    role: roleSchema,
    status: z.enum(['active', 'disabled']),
    lastLoginAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const setupStatusSchema = z.object({
    /** True when no superadmin exists yet and the bootstrap flow is open. */
    needsBootstrap: z.boolean(),
});
export const sessionResponseSchema = z.object({
    user: publicUserSchema,
});
export const buildInfoSchema = z.object({
    /** Display label, e.g. "0.1.0+139d35b5" — or just "0.1.0" on an unknown build. */
    version: z.string(),
    /** Semantic version from package.json. */
    semver: z.string(),
    /** Short commit sha the image was built from, or "unknown". */
    commit: z.string(),
    /** ISO timestamp baked in at image build time, when the builder supplied one. */
    builtAt: z.string().nullable(),
});
export const healthResponseSchema = z.object({
    status: z.enum(['ok', 'degraded']),
    /** Kept at the top level for existing probes and dashboards. */
    version: z.string(),
    build: buildInfoSchema,
    uptimeSeconds: z.number(),
    database: z.object({
        reachable: z.boolean(),
        latencyMs: z.number().nullable(),
        timescale: z.boolean(),
    }),
});
/**
 * Composes the human-facing build label. Kept as a pure function so both sides
 * format it identically and it can be tested without touching the environment.
 */
export function formatVersion(semver, commit) {
    const clean = commit.trim();
    if (!clean || clean === 'unknown')
        return semver;
    return `${semver}+${clean}`;
}
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
//# sourceMappingURL=schemas.js.map